import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Autocomplete,
  IconButton,
  MenuItem,
  Divider,
  Alert,
} from '@mui/material';
import {
  ArrowBack,
  Save,
  Delete,
  Search,
  Business,
  LocalPharmacy,
} from '@mui/icons-material';
import { itemsAPI, suppliersAPI, purchaseAPI } from '../../services/API';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../../../INV/pharmecy/contexts/AuthContext';

export const InvEditPurchase = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [availableItems, setAvailableItems] = useState([]);
  const [items, setItems] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expectedDelivery, setExpectedDelivery] = useState('');
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('draft');
  const [poNumber, setPoNumber] = useState('');
const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [purchaseRes, suppliersRes, itemsRes] = await Promise.all([
        purchaseAPI.getById(id),
        suppliersAPI.getAll(),
        itemsAPI.getAll()
      ]);

      const purchaseOrder = purchaseRes.data.data.purchaseOrder;
      setSuppliers(suppliersRes.data.data.suppliers);
      setAvailableItems(itemsRes.data.data.items);

      // Set form data from existing purchase order
      setSelectedSupplier(purchaseOrder.supplier);
      setPoNumber(purchaseOrder.poNumber);
      setStatus(purchaseOrder.status);
      setNotes(purchaseOrder.notes || '');
      setExpectedDelivery(
        purchaseOrder.expectedDeliveryDate 
          ? purchaseOrder.expectedDeliveryDate.split('T')[0] 
          : ''
      );

      // Transform items to match the create page structure
      const transformedItems = purchaseOrder.items.map(orderItem => ({
        item: orderItem.item,
        quantity: orderItem.quantity,
        costPrice: orderItem.costPrice,
        sellingPrice: orderItem.sellingPrice,
        mrp: orderItem.mrp,
        taxRate: orderItem.taxRate,
      }));

      setItems(transformedItems);
    } catch (error) {
      enqueueSnackbar('Error fetching data', { variant: 'error' });
      navigate('/purchases');
    } finally {
      setLoading(false);
    }
  };

  const addItem = (newItem) => {
    const existingItem = items.find(item => item.item._id === newItem._id);
    
    if (existingItem) {
      setItems(items.map(item =>
        item.item._id === newItem._id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setItems([...items, {
        item: newItem,
        quantity: 1,
        costPrice: 0,
        sellingPrice: 0,
        mrp: 0,
        taxRate: 18,
      }]);
    }
    
    setSearchTerm('');
    enqueueSnackbar(`${newItem.name} added to order`, { variant: 'success' });
  };

  const validateForm = () => {
  const newErrors = {};
  
  if (!selectedSupplier) {
    newErrors.supplier = 'Supplier is required';
  }
  
  if (items.length === 0) {
    newErrors.items = 'At least one item is required';
  }
  
  // Validate item prices
  items.forEach((item, index) => {
    if (item.costPrice <= 0) {
      newErrors[`item_${index}_costPrice`] = 'Cost price must be greater than 0';
    }
    if (item.sellingPrice <= 0) {
      newErrors[`item_${index}_sellingPrice`] = 'Selling price must be greater than 0';
    }
    if (item.mrp <= 0) {
      newErrors[`item_${index}_mrp`] = 'MRP must be greater than 0';
    }
    if (item.sellingPrice > item.mrp) {
      newErrors[`item_${index}_pricing`] = 'Selling price cannot exceed MRP';
    }
  });
  
  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};

  const removeItem = (itemId) => {
    setItems(items.filter(item => item.item._id !== itemId));
  };

  const updateItem = (itemId, field, value) => {
  setItems(items.map(item =>
    item.item._id === itemId
      ? { ...item, [field]: parseFloat(value) || 0 }
      : item
  ));
};


  const updateQuantity = (itemId, quantity) => {
  if (quantity < 1) {
    removeItem(itemId);
    return;
  }
  
  setItems(items.map(item =>
    item.item._id === itemId
      ? { ...item, quantity }
      : item
  ));
};


  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.costPrice), 0);
    const tax = items.reduce((sum, item) => sum + (item.quantity * item.costPrice * (item.taxRate / 100)), 0);
    const grandTotal = subtotal + tax;
    
    return { subtotal, tax, grandTotal };
  };

  const handleUpdateOrder = async () => {
  if (!validateForm()) {
    enqueueSnackbar('Please fix validation errors', { variant: 'error' });
    return;
  }

  setSaving(true);
  try {
    const totals = calculateTotals();
    const orderData = {
      supplier: selectedSupplier._id,
      items: items.map(item => ({
        item: item.item._id,
        quantity: item.quantity,
        costPrice: item.costPrice,
        sellingPrice: item.sellingPrice,
        mrp: item.mrp,
        taxRate: item.taxRate,
      })),
      expectedDeliveryDate: expectedDelivery || undefined,
      notes: notes || undefined,
      status: status,
      poNumber: poNumber,
      totalAmount: totals.subtotal,
      taxAmount: totals.tax,
      grandTotal: totals.grandTotal,
      updatedBy: user._id
    };

    await purchaseAPI.update(id, orderData);
    enqueueSnackbar('Purchase order updated successfully!', { variant: 'success' });
    navigate('/hospital/hospital-inventory/purchases');
  } catch (error) {
    console.error('Update error:', error);
    const errorMessage = error.response?.data?.message || 'Error updating purchase order';
    enqueueSnackbar(errorMessage, { variant: 'error' });
  } finally {
    setSaving(false);
  }
};

  const { subtotal, tax, grandTotal } = calculateTotals();

  const filteredItems = availableItems.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/hospital/hospital-inventory/purchases')}
          >
            Back
          </Button>
          <Typography variant="h4" fontWeight="bold">
            Edit Purchase Order: {poNumber}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            onClick={() => navigate('/hospital/hospital-inventory/purchases')}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            startIcon={<Save />}
            variant="contained"
            onClick={handleUpdateOrder}
            disabled={saving || !selectedSupplier || items.length === 0}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Left Column - Supplier & Medicine Search */}
        <Grid item xs={12} md={8}>
          {/* Supplier Selection */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Supplier Information
              </Typography>
              <Autocomplete
                options={suppliers}
                getOptionLabel={(option) => 
                  `${option.name} (${option.contactPerson})`
                }
                value={selectedSupplier}
                onChange={(event, newValue) => setSelectedSupplier(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Supplier"
                    placeholder="Search supplier by name"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <Business sx={{ mr: 1, color: 'text.secondary' }} />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              
              {selectedSupplier && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                  <Typography variant="body2">
                    <strong>Contact:</strong> {selectedSupplier.contactPerson}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Phone:</strong> {selectedSupplier.phone}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Email:</strong> {selectedSupplier.email}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Lead Time:</strong> {selectedSupplier.leadTime} days
                  </Typography>
                  <Typography variant="body2">
                    <strong>Address:</strong> {selectedSupplier.address?.street}, {selectedSupplier.address?.city}, {selectedSupplier.address?.state} - {selectedSupplier.address?.zipCode}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Medicine Search */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Add Items
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search items by name or generic name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <Search sx={{ mr: 1, color: 'text.secondary' }} />
                  ),
                }}
              />

              {searchTerm && (
                <Box sx={{ mt: 2, maxHeight: 200, overflow: 'auto' }}>
                  {filteredItems.slice(0, 5).map((item) => (
                    <Box
                      key={item._id}
                      sx={{
                        p: 2,
                        mb: 1,
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                      onClick={() => addItem(item)}
                    >
                      <Typography variant="subtitle2" fontWeight="bold">
                        {item.name}
                      </Typography>
                      <Typography variant="body2"  color="text.secondary">
                        {item.brand} • {item.itemType}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Order Items */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Order Items
              </Typography>
              
              {items.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <LocalPharmacy sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    No items in order. Search and add items above.
                  </Typography>
                </Box>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Item</TableCell>
                        <TableCell align="center">Quantity</TableCell>
                        <TableCell align="center">Cost Price</TableCell>
                        <TableCell align="center">Selling Price</TableCell>
                        <TableCell align="center">MRP</TableCell>
                        <TableCell align="center">Tax Rate</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell align="center">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((orderItem) => (
                        <TableRow key={orderItem.item._id}>
                          <TableCell>
                            <Box>
                              <Typography variant="subtitle2" fontWeight="bold">
                                {orderItem.item.name}
                              </Typography>
                              
                              <Typography variant="body2" color="text.secondary">
                                {orderItem.item.brand} • {orderItem.item.itemType}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                              <IconButton
                                size="small"
                                onClick={() => updateQuantity(orderItem.item._id, orderItem.quantity - 1)}
                              >
                                -
                              </IconButton>
                              <Typography variant="body1" sx={{ minWidth: 30, textAlign: 'center' }}>
                                {orderItem.quantity}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => updateQuantity(orderItem.item._id, orderItem.quantity + 1)}
                              >
                                +
                              </IconButton>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <TextField
                              size="small"
                              type="number"
                              value={orderItem.costPrice}
                              onChange={(e) => updateItem(orderItem.item._id, 'costPrice', e.target.value)}
                              sx={{ width: 80 }}
                              inputProps={{ min: 0, step: 0.01 }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <TextField
                              size="small"
                              type="number"
                              value={orderItem.sellingPrice}
                              onChange={(e) => updateItem(orderItem.item._id, 'sellingPrice', e.target.value)}
                              sx={{ width: 80 }}
                              inputProps={{ min: 0, step: 0.01 }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <TextField
                              size="small"
                              type="number"
                              value={orderItem.mrp}
                              onChange={(e) => updateItem(orderItem.item._id, 'mrp', e.target.value)}
                              sx={{ width: 80 }}
                              inputProps={{ min: 0, step: 0.01 }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <TextField
                              size="small"
                              type="number"
                              value={orderItem.taxRate}
                              onChange={(e) => updateItem(orderItem.item._id, 'taxRate', e.target.value)}
                              sx={{ width: 70 }}
                              inputProps={{ min: 0, max: 100, step: 0.01 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="subtitle1" fontWeight="bold">
                              ₹{(orderItem.quantity * orderItem.costPrice).toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              color="error"
                              onClick={() => removeItem(orderItem.item._id)}
                            >
                              <Delete />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Order Summary */}
        <Grid item xs={12} md={4}>
          <Card sx={{ position: 'sticky', top: 100 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Order Summary
              </Typography>

              {/* PO Number */}
              <TextField
                fullWidth
                label="PO Number"
                value={poNumber}
                onChange={(e) => setPoNumber(e.target.value)}
                margin="normal"
                required
              />

              {/* Status */}
              <TextField
                fullWidth
                select
                label="Status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                margin="normal"
              >
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="sent">Sent</MenuItem>
                <MenuItem value="confirmed">Confirmed</MenuItem>
                <MenuItem value="partially_received">Partially Received</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </TextField>

              {selectedSupplier && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Supplier
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {selectedSupplier.name}
                  </Typography>
                </Box>
              )}

              <TextField
                fullWidth
                type="date"
                label="Expected Delivery"
                value={expectedDelivery}
                onChange={(e) => setExpectedDelivery(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                sx={{ mb: 2 }}
              />

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Subtotal:</Typography>
                  <Typography variant="body2">₹{subtotal.toFixed(2)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Tax:</Typography>
                  <Typography variant="body2">₹{tax.toFixed(2)}</Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6" fontWeight="bold">Total:</Typography>
                  <Typography variant="h6" fontWeight="bold" color="primary.main">
                    ₹{grandTotal.toFixed(2)}
                  </Typography>
                </Box>
              </Box>

              <Alert 
                severity={
                  status === 'completed' ? 'success' :
                  status === 'cancelled' ? 'error' :
                  status === 'sent' ? 'info' :
                  status === 'confirmed' ? 'warning' : 'info'
                } 
                sx={{ mb: 2 }}
              >
                {status === 'draft' && 'This order is currently in draft mode.'}
                {status === 'sent' && 'This order has been sent to the supplier.'}
                {status === 'confirmed' && 'This order has been confirmed by the supplier.'}
                {status === 'partially_received' && 'This order has been partially received.'}
                {status === 'completed' && 'This order has been completed.'}
                {status === 'cancelled' && 'This order has been cancelled.'}
              </Alert>

              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleUpdateOrder}
                disabled={saving || !selectedSupplier || items.length === 0}
                sx={{ py: 1.5 }}
              >
                {saving ? 'Updating...' : `Update Purchase Order - ₹${grandTotal.toFixed(2)}`}
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};